import React from "react";
import {useTranslation} from "react-i18next";
import {useGroupDetailsForm} from "../hooks";
import ARIcon from "../../../components/ARIcon";
import {getTableNoItemsView} from "../../../utils";
import PhoneLink from "../../../components/PhoneLink";
import {getTableColumnHeaders} from "./GroupDetails.model";
import AddButtonRow from "../../../components/AddButtonRow";
import {defaultAvatarSource} from "../../../utils/constants";
import {formatPhoneNumber} from "../../../utils/phoneHandlers";
import {withErrorBoundary} from "../../../components/ErrorBoundary";
import FormControlsButtons from "../../../components/FormControlButtons";
import {ARInputWithLabel} from "../../../components/ReactHookForm/fields";
import {
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
  CDataTable,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CForm,
  CImg,
  CRow
} from "@coreui/react";
import "./GroupDetails.scss";

const GroupDetails = () => {
  const {t} = useTranslation();
  const {
    Prompt,
    header,
    formControl,
    groupMembers,
    onFormReset,
    onFormSubmit,
    showMembersModal,
    onDeleteGroupClick,
    onDeleteMemberClick
  } = useGroupDetailsForm();

  const {handleSubmit, control} = formControl;

  return (
    <CCard className="GroupDetailsCard">
      <CCardHeader className="GroupDetailsCard__header">
        <h1 className="GroupDetailsCard__header-text card-header-text">
          {header}
        </h1>
      </CCardHeader>
      <CCardBody className="GroupDetailsCard__body">
        <Prompt/>
        <CForm id="group-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
          <ARInputWithLabel
            id="name"
            name="name"
            control={control}
            autoComplete="off"
            labelText={t("form:label.group", "Group name")}
            placeholder={t("form:placeholder.group", "Enter group's name")}
          />
        </CForm>
      </CCardBody>
      <CCardFooter className="GroupDetailsCard__footer">
        <FormControlsButtons
          className="mr-3"
          form="group-edit-form"
          onCancelClick={onFormReset}
          onDeleteClick={onDeleteGroupClick}
        />
      </CCardFooter>
      <CCard className="mr-3 ml-3">
        <CCardBody>
          <CRow className="justify-content-between card-header-text">
            <h4>{t("card.title.members", "Members")}</h4>
            <AddButtonRow onClick={showMembersModal}/>
          </CRow>
          {
            !!groupMembers.members?.length &&
            <CDataTable
              items={groupMembers.members}
              addTableClasses="members-table"
              fields={getTableColumnHeaders()}
              noItemsView={getTableNoItemsView()}
              hover
              striped
              border
              scopedSlots={
                {
                  "firstName":
                    (item) => (
                      <td className="members-table__cell">
                        <div className="user-info-content">
                          <div className="user-info-content__c-avatar c-avatar">
                            <CImg
                              src={item.avatar || defaultAvatarSource}
                              className="c-avatar__image c-avatar-img"
                              alt="member"
                            />
                          </div>
                          <div className="user-info-content__text">
                            {item.first_name || item.email || ""}
                          </div>
                        </div>
                      </td>
                    ),
                  "lastName":
                    (item) => (
                      <td className="members-table__cell">
                        {item.last_name || ""}
                      </td>
                    ),
                  "phone":
                    (item) => (
                      <td className="members-table__cell">
                        <div className="members-table__phone">
                          {
                            item.phone &&
                            <PhoneLink className="link-content">{formatPhoneNumber(item.phone)}</PhoneLink>
                          }
                        </div>
                      </td>
                    ),
                  "email":
                    (item) => (
                      <td className="members-table__cell">
                        {
                          item.email &&
                          <a href={`mailto: ${item.email}`} onClick={(event) => event.stopPropagation()}>
                            <div className="link-content">
                              {item.email}
                            </div>
                          </a>
                        }
                      </td>
                    ),
                  "actionsColumn":
                    (item) => (
                      <td className="members-table__cell">
                        <CDropdown className="dropdown_no-caret">
                          <CDropdownToggle
                            className="btn-block"
                            size="sm"
                            color="dark"
                            variant="outline"
                          >
                            <ARIcon name="cilMinus" size="sm"/>
                          </CDropdownToggle>
                          <CDropdownMenu placement="left">
                            <CDropdownItem onClick={() => onDeleteMemberClick(item.id)}>
                              {t("button.delete", "Delete")}
                            </CDropdownItem>
                          </CDropdownMenu>
                        </CDropdown>
                      </td>
                    )
                }
              }
            />
          }
        </CCardBody>
      </CCard>
    </CCard>
  );
};

export default withErrorBoundary(GroupDetails);